import type { ScreenerTickerData } from '@/types'
import { historyWindowCovers, type ChartTimeRange } from './screenerHistory'

type Entry = {
  scope: string
  data: ScreenerTickerData
  range: ChartTimeRange
  requestedSpan: number
  insertedAt: number
  points: number
  bytes: number
}

/** Per-workspace LRU, bounded independently of the number of visited windows.
 * Immutable response snapshots are shared with the displayed chart. The byte
 * budget estimates retained objects from each series' sampled point shape;
 * the independent point cap avoids serializing a whole week on each live tick.
 */
export class ScreenerHistoryCache {
  private entries: Entry[] = []
  // A seven-day/5m response for twelve venues and all four history metrics has
  // about 97k points; keep one complete week while still bounding memory.
  constructor(private limits = { windows: 8, points: 120_000, bytes: 64 * 1024 * 1024, ageMs: 300_000 }) {}

  invalidate(symbol: string, from?: number | null): void {
    this.entries = this.entries.filter(entry => entry.data.symbol.toUpperCase() !== symbol.toUpperCase()
      || (from != null && entry.range.to < from))
  }

  get(scope: string, range: ChartTimeRange, interval: string): ScreenerTickerData | null {
    const now = Date.now()
    this.entries = this.entries.filter(entry => now - entry.insertedAt <= this.limits.ageMs)
    const index = this.entries.findIndex(entry => entry.scope === scope
      && historyWindowCovers(entry.range, range)
      && (entry.data.oi_interval === interval || range.to - range.from >= entry.requestedSpan / 2))
    if (index < 0) return null
    const [entry] = this.entries.splice(index, 1)
    this.entries.unshift(entry)
    return entry.data
  }

  put(scope: string, data: ScreenerTickerData, requestedSpan: number): void {
    if (data.oi_history_load && data.oi_history_load.status !== 'complete') return
    const window = data.history_window
    if (!window || window.truncated || !Number.isFinite(window.from_ts) || !Number.isFinite(window.to_ts)) return
    const range = { from: window.from_ts, to: window.to_ts }
    let points = 0
    let bytes = 1024
    for (const exchange of Object.values(data.exchanges)) {
      bytes += 2048
      for (const series of [exchange.oi_history, exchange.volume_history, exchange.trade_price_history,
        exchange.candles, exchange.cvd_history]) {
        if (!series?.length) continue
        points += series.length
        const samples = [series[0], series[Math.floor(series.length / 2)], series[series.length - 1]]
        bytes += series.length * (64 + Math.max(...samples.map(point => JSON.stringify(point).length * 2)))
      }
    }
    if (points > this.limits.points) return
    if (bytes > this.limits.bytes) return
    // Updating the same rolling buffer must not evict every historical visit.
    const tolerance = Math.max(1, (range.to - range.from) * 0.1)
    this.entries = this.entries.filter(entry => !(entry.scope === scope
      && entry.data.oi_interval === data.oi_interval
      && Math.abs(entry.range.from - range.from) <= tolerance
      && Math.abs(entry.range.to - range.to) <= tolerance))
    this.entries.unshift({ scope, data, range, requestedSpan, insertedAt: Date.now(), points, bytes })
    while (this.entries.length > this.limits.windows
      || this.entries.reduce((sum, entry) => sum + entry.points, 0) > this.limits.points
      || this.entries.reduce((sum, entry) => sum + entry.bytes, 0) > this.limits.bytes) this.entries.pop()
  }
}
